// Testimonials — read-first dashboard promo (PULL surface). A slim, evergreen
// strip that surfaces a REAL published student quote as social proof, with a
// one-tap path to the full /testimonials feed.
//
// LOOK: deliberately NOT the dark-green "live event" strip (that implied urgency
// + a countdown, which would be a lie here). This is a light citrus card in the
// Lomi brand (lime #84cc16 / #4a7c10), so it reads as warmth/motivation, not an
// alarm — the right tone for cram week.
//
// FRAMING: read-first, not share-first. During the last week before ESSLCE the
// goal is motivation (peers succeeded with this), not pulling focus into a
// writing task. The "Read stories" CTA is primary; sharing happens on the page.
//
// SELF-GATING: fetches GET /testimonials and renders nothing if there are no
// published stories yet, so it can ship dark-safe. DISMISS: per-session only
// (sessionStorage) — seen once a session, gone for the rest of it, back the next
// day. Unlike the Live banner it is NOT permanently sticky (evergreen content
// shouldn't nag a cramming student) nor permanently dismissed.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { track } from '../services/api';
import { testimonialsAPI, Testimonial } from '../services/testimonials';
import { useTheme } from '../contexts/ThemeContext';

const DISMISS_KEY = 'lomi_testimonials_promo_dismissed';

// Bilingual by design — shown to EVERY student regardless of locale setting
// (founder preference: Amharic with English alongside, not one-or-the-other).
const INVITE = 'ሎሚን ይወዳሉ? (Do you like Lomi?)';
const CTA = 'ሐሳብዎን ይስጡ · Give your opinion →';

type Step = 'loading' | 'hidden' | 'show';

// ROTATION: the team hand-picks the banner pool in tutor-portal (each pinned
// quote comes back from GET /testimonials?banner=true). We show a RANDOM one of
// them per render, so a returning student sees the pool rotate rather than the
// same quote every visit. Audio/video have no inline quote to render in a
// one-line strip, so they're skipped for the highlight.
const pickQuote = (items: Testimonial[]): Testimonial | null => {
  const withQuote = items.filter((t) => t.media_type === 'text' && (t.quote || '').trim());
  if (!withQuote.length) return null;
  return withQuote[Math.floor(Math.random() * withQuote.length)];
};

const TestimonialsPromo: React.FC<{ source?: string }> = ({ source = 'dashboard' }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [step, setStep] = useState<Step>('loading');
  const [quote, setQuote] = useState<Testimonial | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) === '1') {
      setStep('hidden');
      return;
    }
    let cancelled = false;
    testimonialsAPI
      .list({ banner: true, limit: 30 })
      .then((res) => {
        if (cancelled) return;
        if (!res.total) {
          setStep('hidden');
          return;
        }
        setQuote(pickQuote(res.testimonials));
        track('testimonials_promo.shown', { surface: source, total: res.total });
        setStep('show');
      })
      .catch(() => setStep('hidden'));
    return () => {
      cancelled = true;
    };
  }, [source]);

  if (step === 'loading' || step === 'hidden') return null;

  const go = () => {
    track('testimonials_promo.cta_clicked', { surface: source });
    navigate('/testimonials');
  };
  const dismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    track('testimonials_promo.dismissed', { surface: source });
    sessionStorage.setItem(DISMISS_KEY, '1');
    setStep('hidden');
  };

  // Keep the highlighted quote to one tidy line on the strip.
  const snippet = (() => {
    const q = (quote?.quote || '').trim().replace(/\s+/g, ' ');
    if (!q) return '';
    return q.length > 90 ? `${q.slice(0, 87)}…` : q;
  })();

  // ---- light citrus card (Lomi brand: lime #84cc16 / #4a7c10) ----
  // Dark variant: tinted-on-card fill, brighter lime for the accent/chip so the
  // strip reads on a dark page instead of glaring white.
  const card: React.CSSProperties = {
    position: 'relative',
    background: dark
      ? 'linear-gradient(135deg, rgba(134,194,50,0.10) 0%, #171b21 100%)'
      : 'linear-gradient(135deg, #f3f9ec 0%, #ffffff 100%)',
    border: dark ? '1px solid #262c35' : '1px solid #cfe6b0',
    borderRadius: 15,
    padding: '0.55rem 0.9rem',
    margin: '0 0 0.85rem',
    cursor: 'pointer',
    boxShadow: dark ? '0 10px 26px -22px rgba(0,0,0,0.6)' : '0 10px 26px -22px rgba(74,124,16,0.5)',
    overflow: 'hidden',
  };
  const quoteLine: React.CSSProperties = {
    fontFamily: "'Fraunces', Georgia, serif",
    fontWeight: 600,
    fontSize: '0.9rem',
    lineHeight: 1.35,
    color: dark ? '#e6e8eb' : '#1f2d3d',
  };
  const mainRow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 6,
  };
  const attribution: React.CSSProperties = {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: dark ? '#aab1ba' : '#7f8c8d',
  };
  const inviteLine: React.CSSProperties = {
    minWidth: 0,
    fontSize: '0.82rem',
    fontWeight: 700,
    color: dark ? '#86c232' : '#4a7c10',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const fallbackLine: React.CSSProperties = {
    fontFamily: "'Fraunces', Georgia, serif",
    fontWeight: 600,
    fontSize: '0.9rem',
    color: dark ? '#e6e8eb' : '#1f2d3d',
  };
  const ctaChip: React.CSSProperties = {
    flexShrink: 0,
    padding: '0.45rem 0.95rem',
    borderRadius: 999,
    background: dark ? '#86c232' : '#4a7c10',
    color: dark ? '#12161c' : '#fff',
    fontSize: '0.85rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  };
  const closeBtn: React.CSSProperties = {
    position: 'absolute',
    top: 4,
    right: 8,
    background: 'none',
    border: 'none',
    color: dark ? '#7d8590' : '#9aa7b0',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 4,
  };

  return (
    <div style={card} onClick={go} role="button" tabIndex={0}>
      <button onClick={dismiss} style={closeBtn} aria-label="Dismiss">
        ×
      </button>

      {snippet ? (
        <>
          <div style={quoteLine}>
            🌟 “{snippet}”
            {quote?.author_name ? <span style={attribution}> — {quote.author_name}</span> : null}
          </div>
          <div style={mainRow}>
            <span style={inviteLine}>{INVITE}</span>
            <span style={ctaChip}>{CTA}</span>
          </div>
        </>
      ) : (
        <div style={mainRow}>
          <span style={fallbackLine}>🌟 {INVITE}</span>
          <span style={ctaChip}>{CTA}</span>
        </div>
      )}
    </div>
  );
};

export default TestimonialsPromo;
